import { Injectable } from "@angular/core";
import { PhotosphereRepositoryInterface } from "./photosphere.interface";
import { RemoteRepositoryInterface } from "../../remote/remote.interface";
import { forkJoin, Observable, of } from "rxjs";
import { concatMap, map } from "rxjs/operators";
import { PhotosphereList } from "../../entities/photospherelist.model";
import { MediaList } from "../../entities/medialist.model";
import { MarkerType } from "../../../crossconcern/types/marker.type";
import { MarkerModel } from "../../entities/marker.model";
import { PhotosphereModel } from "../../entities/photosphere.model";
import { PhotosphereType } from "../../../crossconcern/types/photosphere.type";
import { PHOTOSPHERE_IMAGE_TAG } from "../../../crossconcern/utilities/properties/base.property";

@Injectable()
export class PhotosphereRepository implements PhotosphereRepositoryInterface {

    constructor(private remoteRepository: RemoteRepositoryInterface) {}

    public getPhotospheres(pageSize: number, page: number, nurseryUUID?: string, withNursery = false, withMedia = false)
        : Observable<PhotosphereList> {
        let url = "photospheres";
        if(nurseryUUID) {
            url = url.concat("?search=nursery_uuid:" + nurseryUUID);
            if(pageSize != null && page != null) {
                url += "&size=" + pageSize + "&page=" + page + "&withNursery=" + withNursery;
            }
        } else if(pageSize != null && page != null) {
            url += "?size=" + pageSize + "&page=" + page + "&withNursery=" + withNursery;
        }
        return this.remoteRepository.get(url).pipe(
            concatMap((response) => {
                const photosphereList = new PhotosphereList(response as JSON);
                if (withMedia && photosphereList.photospheres) {
                    const obs = [];
                    for (const photosphere of photosphereList.photospheres) {
                        obs.push(this.getPhotosphereMedia(photosphere.uuid));
                    }
                    return forkJoin(obs)
                        .pipe(
                            map((medias: MediaList[]) => {
                                for(const media of medias) {
                                    if(media.medias) {
                                        for (const ps of photosphereList.photospheres) {
                                            if (ps.uuid === media.medias[0].entityUUID) {
                                                const mediaURL = media.medias[0].getMediaURL("view");
                                                if(mediaURL) {
                                                    ps.mediaURL = mediaURL.replace("{?res}", "");
                                                }
                                                break;
                                            }
                                        }
                                    }
                                }
                                return photosphereList;
                            })
                        );
                }
                return of(photosphereList);
            })
        );
    }

    public getPhotosphereMedia(uuid: string): Observable<MediaList> {
        const url = "media/entities/" + uuid
            + "?search=custom_tag:" + PHOTOSPHERE_IMAGE_TAG;
        return this.remoteRepository.get(url).pipe(
            map((response) => new MediaList(response as JSON))
        );
    }

    public updatePhotosphere(uuid: string, photosphere: PhotosphereType): Observable<PhotosphereModel> {
        return this.remoteRepository.update("photospheres/" + uuid, photosphere).pipe(
            map(((response) => new PhotosphereModel(response as JSON)))
        );
    }

    public associatePhotosphereProduct(photosphereUuid: string, productUuid: string): Observable<PhotosphereModel> {
        return this.remoteRepository.update("photospheres/" + photosphereUuid + "/products/" + productUuid, {}).pipe(
            map(((response) => new PhotosphereModel(response as JSON)))
        );
    }

    public disassociatePhotosphereProducts(photosphereUuid: string): Observable<PhotosphereModel> {
        return this.remoteRepository.update("photospheres/" + photosphereUuid + "/disassociateProducts", {}).pipe(
            map(((response) => new PhotosphereModel(response as JSON)))
        );
    }

    public deletePhotosphere(uuid: string): Observable<{}> {
        return this.remoteRepository.delete("photospheres/" + uuid);
    }

    public getMarker(markerUUID: string): Observable<MarkerModel> {
        return this.remoteRepository.get("markers/" + markerUUID)
            .pipe(
                map(((response) => new MarkerModel(response as JSON)))
            );
    }

    public addMarker(marker: MarkerType): Observable<MarkerModel> {
        return this.remoteRepository.post("markers", marker).pipe(
            map(((response) => new MarkerModel(response as JSON)))
        );
    }

    public updateMarker(markerUUID: string, marker: MarkerType): Observable<MarkerModel> {
        return this.remoteRepository.update("markers/" + markerUUID, marker).pipe(
            map(((response) => new MarkerModel(response as JSON)))
        );
    }

    public deleteMarker(markerUUID: string): Observable<{}> {
        return this.remoteRepository.delete("markers/" + markerUUID);
    }
}